import type { VoidFunctionComponent } from 'react';

import { LabeledSelect } from '../../../../../../../generic/Forms';
import { QualityRequired, SecurityRequired } from '../../../../../../../generic/Gates';
import { RouterLink } from '../../../../../../../generic/Link';
import { UNSET_POLICY_OPTION } from '../../../../../../../shared/policies/hooks/usePolicies';
import type { UsePoliciesSelectionProps } from '../../../../hooks/usePoliciesSelection';

import styles from '../../../ReleaseGroupsRevamp.module.less';

/**
 * PoliciesSelection displays the dropdowns to allow the user
 * to select the policies for the release group.
 */
const PoliciesSelection: VoidFunctionComponent<UsePoliciesSelectionProps> = ({
  policies,
  selectedPolicies,
  isLoading,
  onUpdateSelectedPolicies,
  shouldDisplayLicensingPolicies,
  shouldDisplaySecurityPolicies,
  shouldDisplayQualityPolicies,
}) => {
  //  I couldn't imagine this ever happening, but this could guard against displaying
  //  the "Policies" section without the actually dropdowns.
  if (!shouldDisplayLicensingPolicies && !shouldDisplaySecurityPolicies && !shouldDisplayQualityPolicies) {
    return null;
  }

  return (
    <section className={styles.section}>
      <div className={styles.detailsTitle}>
        Policies
      </div>
      <div className={styles.detailsDescription}>
        Select policies to apply to all projects in this release group.
        {' '}
        Visit
        {' '}
        <RouterLink to="/policies" className={styles.detailsLink} openInNewTab>
          Policies
        </RouterLink>
        {' '}
        to manage and create new policies.
      </div>
      <div className={styles.policies}>
        {shouldDisplayLicensingPolicies && (
          <div className="width-full">
            <LabeledSelect
              id="licensing-policy"
              label="Licensing Policy"
              options={policies.licensing}
              value={selectedPolicies.licensing}
              // Technically Select could return null - we'll use null to represent unset.
              onChange={option =>
                onUpdateSelectedPolicies({ licensing: option === null ? UNSET_POLICY_OPTION : option })}
              isDisabled={isLoading}
            />
          </div>
        )}
        {shouldDisplaySecurityPolicies && (
          <SecurityRequired>
            <div className="width-full">
              <LabeledSelect
                id="security-policy"
                label="Security Policy"
                options={policies.vulnerability}
                value={selectedPolicies.vulnerability}
                // Technically Select could return null - we'll use null to represent unset.
                onChange={option =>
                  onUpdateSelectedPolicies({ vulnerability: option === null ? UNSET_POLICY_OPTION : option })}
                isDisabled={isLoading}
              />
            </div>
          </SecurityRequired>
        )}
        {shouldDisplayQualityPolicies && (
          <QualityRequired>
            <div className="width-full">
              <LabeledSelect
                id="quality-policy"
                label="Quality Policy"
                options={policies.quality}
                value={selectedPolicies.quality}
                // Technically Select could return null - we'll use null to represent unset.
                onChange={option =>
                  onUpdateSelectedPolicies({ quality: option === null ? UNSET_POLICY_OPTION : option })}
                isDisabled={isLoading}
              />
            </div>
          </QualityRequired>
        )}
      </div>
    </section>
  );
};

export default PoliciesSelection;
